// Feature 12: Insight Explainability Page

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Refresh,
  Warning,
  Info,
  ExpandMore,
  Psychology,
} from '@mui/icons-material';
import {
  getInsightExplanation,
  recomputeInsightExplanation,
  getAlternativeExplanations,
} from '../services/insightExplainabilityService';
import CausalityBadge from '../components/insightExplainability/CausalityBadge';
import ProvenanceDAG from '../components/insightExplainability/ProvenanceDAG';
import EvidenceChain from '../components/insightExplainability/EvidenceChain';

const InsightExplainabilityPage: React.FC = () => {
  const { insightId } = useParams<{ insightId: string }>();
  const queryClient = useQueryClient();
  const [showAlternatives, setShowAlternatives] = useState(false);

  // Fetch insight explanation
  const { data: explanation, isLoading, error } = useQuery({
    queryKey: ['insightExplanation', insightId],
    queryFn: () => getInsightExplanation(insightId!),
    enabled: !!insightId,
  });

  // Fetch alternatives (lazy load)
  const { data: alternatives } = useQuery({
    queryKey: ['insightAlternatives', insightId],
    queryFn: () => getAlternativeExplanations(insightId!),
    enabled: showAlternatives && !!insightId,
  });

  // Recompute mutation
  const recomputeMutation = useMutation({
    mutationFn: () => recomputeInsightExplanation(insightId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insightExplanation', insightId] });
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !explanation) {
    return (
      <Alert severity="error">
        Failed to load insight explanation. Please try again later.
      </Alert>
    );
  }

  const confidencePercent = Math.round(explanation.overallConfidence * 100);

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box flex={1}>
          <Typography variant="h4" gutterBottom>
            Insight Explainability
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
            "{explanation.insightText}"
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={() => recomputeMutation.mutate()}
          disabled={recomputeMutation.isPending}
          variant="outlined"
        >
          Recompute
        </Button>
      </Box>

      {/* Causality & Confidence Summary */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Causality Assessment
              </Typography>
              <CausalityBadge level={explanation.causalityLevel} confidence={explanation.overallConfidence} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Overall Confidence
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h4" color="primary">
                  {confidencePercent}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {confidencePercent >= 80
                    ? 'High confidence'
                    : confidencePercent >= 60
                    ? 'Moderate confidence'
                    : 'Low confidence'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Provenance DAG */}
        <Grid item xs={12}>
          <ProvenanceDAG dag={explanation.provenance} />
        </Grid>

        {/* Evidence Chain */}
        <Grid item xs={12}>
          <EvidenceChain evidence={explanation.evidenceChain} />
        </Grid>

        {/* Assumptions & Limitations */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Info color="info" />
                <Typography variant="h6">Assumptions</Typography>
              </Box>
              {explanation.assumptions && explanation.assumptions.length > 0 ? (
                <List dense>
                  {explanation.assumptions.map((assumption, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={assumption}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No explicit assumptions documented.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Warning color="warning" />
                <Typography variant="h6">Limitations</Typography>
              </Box>
              {explanation.limitations && explanation.limitations.length > 0 ? (
                <List dense>
                  {explanation.limitations.map((limitation, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={limitation}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No limitations identified.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Alternative Explanations */}
        <Grid item xs={12}>
          <Accordion
            expanded={showAlternatives}
            onChange={() => setShowAlternatives(!showAlternatives)}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={1}>
                <Psychology />
                <Typography variant="h6">Alternative Explanations</Typography>
                <Chip label="Optional" size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {alternatives && alternatives.explanations && alternatives.explanations.length > 0 ? (
                <Stack spacing={1.5}>
                  {alternatives.explanations.map((alt: string, index: number) => (
                    <Alert
                      key={index}
                      severity="info"
                      icon={false}
                      sx={{ alignItems: 'flex-start' }}
                    >
                      <Box display="flex" gap={2}>
                        <Chip
                          label={`${Math.round((alternatives.confidence[index] || 0) * 100)}%`}
                          size="small"
                          color="info"
                        />
                        <Typography variant="body2">{alt}</Typography>
                      </Box>
                    </Alert>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No alternative explanations available.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box mt={4} pt={2} borderTop={1} borderColor="divider">
        <Typography variant="caption" color="text.secondary">
          Computed: {new Date(explanation.computedAt).toLocaleString()} • Insight ID: {insightId}
        </Typography>
      </Box>
    </Box>
  );
};

export default InsightExplainabilityPage;
